import { FC, useEffect, useRef } from 'react';

import { useSession } from '@eneco/auth';
import { useContent } from '@eneco/cms';
import type { ChatContent } from '@eneco/cms-types/generated/Chat';
import { useApplication } from '@eneco/common/application';
import { useChat, Namespace, ChatArgs, Visibility } from '@eneco/components/chat/useChat';
import { OpenAPI } from '@eneco/dc/client/OpenAPI';
import { useCookieConsentDecision } from '@eneco/hooks/cookieConsent';

type ChatProps = {
  namespace: Namespace;
  fields?: {
    chatNamespace?: {
      id?: string;
      url?: string;
      name?: string;
      displayName?: string;
      fields?: {
        namespace?: {
          value?: Namespace;
        };
      };
    };
    chatProviderTopic?: {
      id?: string;
      url?: string;
      name?: string;
      displayName?: string;
      fields?: {
        namespace?: {
          value?: string;
        };
      };
    };
  };
};

type ChatMountProps = {
  namespace?: Namespace;
  /** The namespace and topic the content names, when the page passes none in the URL. */
  namespaceJSS?: Namespace;
  topicJSS?: string;
};

const ChatMount: FC<ChatMountProps> = ({ namespace, namespaceJSS, topicJSS }) => {
  const hasCookieConsentDecision = useCookieConsentDecision();
  const [mount] = useChat();
  const { searchParams } = useApplication();
  const topicToChat = searchParams.get('topicToChat');
  const textToChat = searchParams.get('textToChat');
  const stateToChat = searchParams.get('stateToChat') as Visibility;

  const { data: session, status } = useSession() ?? {};

  // Remembers the last set of arguments we dispatched to `mount`. This guards
  // against re-dispatching the same topic/message when the effect re-runs for a
  // reason unrelated to a user intent change — most notably next-auth refetching
  // the session when the browser tab regains focus, which changes the `session`
  // reference and would otherwise re-trigger setTopic/sendMessage and re-send an
  // activity to the bot. A genuine change (new deeplink params or remount on
  // navigation) produces a new signature and still dispatches.
  const lastDispatchRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      mount({ visibility: 'hidden' });
    };
  }, [mount]);

  useEffect(() => {
    if (!hasCookieConsentDecision) {
      return;
    }

    if (status === 'loading') {
      return;
    }

    const token = session?.idToken ?? OpenAPI.TOKEN;

    const chatArgs: ChatArgs = {
      askText: textToChat ?? undefined,
      namespace: namespace ?? namespaceJSS,
      topic: topicToChat ?? topicJSS,
      context: textToChat
        ? {
            variables: {
              event: textToChat ? 'start_with_question' : undefined,
            },
          }
        : undefined,
      visibility: stateToChat ?? (textToChat || topicToChat ? 'open' : undefined),
    };

    const dispatchSignature = JSON.stringify({
      namespace: chatArgs.namespace,
      topic: chatArgs.topic,
      askText: chatArgs.askText,
      visibility: chatArgs.visibility,
    });

    // Skip if these exact arguments were already dispatched (e.g. the effect
    // re-ran only because the session reference changed on tab refocus).
    if (lastDispatchRef.current === dispatchSignature) {
      return;
    }
    lastDispatchRef.current = dispatchSignature;

    mount({
      ...chatArgs,
      api: {
        getConversationAuthToken: () => token,
      },
    });
  }, [
    hasCookieConsentDecision,
    mount,
    namespace,
    namespaceJSS,
    session,
    stateToChat,
    status,
    textToChat,
    topicJSS,
    topicToChat,
  ]);

  return null;
};

const Chat: FC<ChatProps> = ({ namespace, fields }) => (
  <ChatMount
    namespace={namespace}
    namespaceJSS={fields?.chatNamespace?.fields?.namespace?.value}
    topicJSS={fields?.chatProviderTopic?.fields?.namespace?.value}
  />
);

/** The `namespace` field of a referenced item, as the Sitecore adapter lifts it. Sanity has no model for it yet. */
const referencedNamespace = (reference: unknown): string | undefined => {
  const value =
    typeof reference === 'object' && reference !== null ? (reference as { namespace?: unknown }).namespace : undefined;
  return typeof value === 'string' ? value : undefined;
};

/**
 * The CMS lane's entry, registered in the containers' `cmsComponents.ts`. It reads the same two references as the
 * Sitecore lane's `fields`. Both point at items the CMS schema does not model yet (unresolved references), so on
 * Sanity they carry no namespace or topic and the chat falls back to its defaults, as on Sitecore when they are unset.
 */
export const ChatInner: FC = () => {
  const content = useContent<ChatContent>();
  return (
    <ChatMount
      namespaceJSS={referencedNamespace(content.chatNamespace) as Namespace | undefined}
      topicJSS={referencedNamespace(content.chatProviderTopic)}
    />
  );
};

export default Chat;
